import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { computed, action } from 'mobx'
import type { SubmittedJob } from '@/features/jobs/details/SubmittedJob'
import ConsultationSchedulerViewModel from './ConsultationSchedulerViewModel'
import { task } from 'mobx-task'
import type { DocumentsListViewModelFactory } from '@/features/docs'
import type { ActivationLifecycle } from '@/infra/activation'
import type { ConsultationStore } from '@/features/jobs/consultations/ConsultationStore'
import type { SnackbarErrorHandlerFactory } from '@/features/shell'
import type { SnackbarFactory } from '@/features/shell/snackbars'

/**
 * View model for managing a consultation, including scheduling and calling.
 */
export default class ConsultationManagerViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The consultation scheduler view model.
   */
  readonly consultationSchedulerVm

  /**
   * Documents list view model used to fetch transcripts.
   */
  readonly documentsListViewModel

  constructor(
    public readonly job: SubmittedJob,
    private readonly _consultationStore: ConsultationStore,
    createDocumentsListViewModel: DocumentsListViewModelFactory,
    private readonly _createSnackbarErrorHandler: SnackbarErrorHandlerFactory,
    newSnackbar: SnackbarFactory,
  ) {
    this.consultationSchedulerVm = new ConsultationSchedulerViewModel(
      this._consultationStore,
      newSnackbar,
      this.job,
      () => this.isSettingAvailabilityAllowed,
    )

    this.documentsListViewModel = createDocumentsListViewModel({
      fetchAuthor: true,
      types: ['GENERATED_TRANSCRIPT'],
      context: {
        type: 'Job',
        jobId: this.job.id,
      },
    })
    this._lifecycle.manage(this.documentsListViewModel)
    this._lifecycle.manage(this.consultationSchedulerVm)
  }

  /**
   * The consultation for the job.
   */
  @computed
  get consultation() {
    return this.job.match({
      consultation: (type) => type.consultation,
      tax: (type) => type.consultation,
      default: () => null,
    })
  }

  /**
   * All generated transcripts for the consultation.
   */
  @computed
  get transcriptVms() {
    return this.documentsListViewModel.allDocuments.filter(
      (vm) => vm.doc.typeId === 'GENERATED_TRANSCRIPT',
    )
  }

  /**
   * Whether the consultation is booked and active.
   */
  @computed
  get isConsultationBookedAndActive() {
    const consultation = this.consultation
    return (
      consultation?.status === 'PendingCall' ||
      consultation?.status === 'InProgress'
    )
  }

  /**
   * Whether the availability can be set.
   */
  @computed
  get isSettingAvailabilityAllowed() {
    return this.job.match({
      consultation: (type) => type.isConsultationSchedulingAllowed,
      tax: (type) => type.isConsultationSchedulingAllowed,
      default: () => false,
    })
  }

  /**
   * Shows the set availability dialog.
   */
  @action.bound
  showSetAvailabilityDialog() {
    this.consultationSchedulerVm.showSetAvailabilityDialog()
  }

  /**
   * Shows the review availability dialog.
   */
  @action.bound
  showReviewAvailabilityDialog() {
    this.consultationSchedulerVm.showReviewAvailabilityDialog()
  }

  /**
   * Opens the video call in a new window.
   */
  joinRoom = task.resolved(async () => {
    if (!this.consultation?.roomId) {
      return
    }

    // Open a new window immedeatly so we don't get flagged as a popup.
    const videoCallWindow = window.open('about:blank', '_blank')

    await this._consultationStore
      .joinRoom(this.consultation.roomId)
      .then((roomUrl) =>
        roomUrl && videoCallWindow
          ? (videoCallWindow.location.href = roomUrl.toString())
          : videoCallWindow?.close(),
      )
      .catch((err) => {
        videoCallWindow?.close()
        throw err
      })
      .catch(
        this._createSnackbarErrorHandler(
          'There was a problem joining the video call',
        ),
      )
  })

  /**
   * Downloads the calendar file for a consultation.
   */
  downloadConsultationCalendar = task.resolved(async () => {
    if (!this.isConsultationBookedAndActive) {
      return
    }

    await this._consultationStore
      .fetchConsultationCalendar(this.job.id)
      .then((file) => {
        // Create a URL for the file
        const url = URL.createObjectURL(file)

        // Create a temporary link element
        const link = document.createElement('a')
        link.href = url
        link.download = 'calendar.ics'

        // Click it bro!
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      })
      .catch(
        this._createSnackbarErrorHandler(
          'There was a problem downloading the calendar event',
        ),
      )
  })

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    void this.documentsListViewModel.fetch(this._lifecycle.signal)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }
}
